import React, { useEffect } from 'react';
import type { CostCentre, Employee, Project, Timesheet, TimesheetEntry, WorkingPattern } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { DataMode } from '@/contexts/DataSourceContext';
import { useTimesheetSettingsData } from './useTimesheetSettingsData';
import { useTimesheetProjectData } from './useTimesheetProjectData';
import { useTimesheetScheduleData } from './useTimesheetScheduleData';
import type { SelectorPreferencesOwner } from '@/hooks/use-selector-preferences';

export interface UseTimesheetDataParams {
  screenMode: DataMode;
  targetEmployeeId: number;
  /** Set when the target is a contractor, whose bookings are looked up by ResourceId. */
  targetResourceId?: number | null;
  currentEmployee: Employee | null;
  currentWeekStart: Date;
  companyId?: string;
  /** Whose favourite / recently used projects are resolved outside the paginated list. */
  selectorPreferencesOwner: SelectorPreferencesOwner | null;
  projects: Project[];
  costCentres: CostCentre[];
  timesheets: Timesheet[];
  entries: TimesheetEntry[];
  setEntries: React.Dispatch<React.SetStateAction<TimesheetEntry[]>>;
  isReadOnly: boolean;
  /** Scheduled bookings wait for this so they reconcile against the refreshed timesheet. */
  isRefreshingTimesheets?: boolean;
  /** Pending auto-save timer, cancelled when the active company changes. */
  autoSaveTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
}

export interface UseTimesheetDataResult {
  organizationSettings: OrganisationTimesheetSettings | undefined;
  employeeWorkingPattern: WorkingPattern | undefined;
  allAvailableProjects: Project[];
  isSearchingProjects: boolean;
  costCentresByProjectCode: Record<string, CostCentre[]>;
  handleProjectSearch: (query: string) => Promise<void>;
  fetchCostCentresForProject: (projectCode: string) => Promise<void>;
  clearFetchedCostCentresCache: () => void;
  hasLoadedScheduledBookings: boolean;
  setHasLoadedScheduledBookings: React.Dispatch<React.SetStateAction<boolean>>;
  hasLoadedAbsences: boolean;
  setHasLoadedAbsences: React.Dispatch<React.SetStateAction<boolean>>;
}

const EMPTY_ENTRIES: TimesheetEntry[] = [];

/**
 * Single entry point for every side-effecting data load the timesheet grid needs:
 * organisation settings and working pattern, all project sources (paginated plus
 * supplementary lookups), scheduled bookings and absences. Entry state stays with
 * the parent orchestrator — this hook only writes to it through `setEntries`.
 */
export function useTimesheetData({
  screenMode,
  targetEmployeeId,
  targetResourceId,
  currentEmployee,
  currentWeekStart,
  companyId,
  selectorPreferencesOwner,
  projects,
  costCentres,
  timesheets,
  entries,
  setEntries,
  isReadOnly,
  isRefreshingTimesheets,
  autoSaveTimerRef,
}: UseTimesheetDataParams): UseTimesheetDataResult {
  const { organizationSettings, employeeWorkingPattern } = useTimesheetSettingsData({
    screenMode,
    targetEmployeeId,
  });

  const {
    allAvailableProjects,
    isSearchingProjects,
    costCentresByProjectCode,
    handleProjectSearch,
    fetchCostCentresForProject,
    clearFetchedCostCentresCache,
    loadInitialProjects,
    resetForCompanyChange: resetProjectsForCompanyChange,
  } = useTimesheetProjectData({
    screenMode,
    projects,
    costCentres,
    companyId,
    selectorPreferencesOwner,
    entries,
    timesheets,
    targetEmployeeId,
    currentWeekStart,
    organizationSettings,
  });

  const {
    hasLoadedScheduledBookings,
    setHasLoadedScheduledBookings,
    hasLoadedAbsences,
    setHasLoadedAbsences,
  } = useTimesheetScheduleData({
    screenMode,
    targetEmployeeId,
    targetResourceId,
    currentWeekStart,
    currentEmployee,
    companyId,
    entries,
    setEntries,
    isReadOnly,
    isRefreshingTimesheets,
    fetchCostCentresForProject,
  });

  // Listen for company-changed event to reset local state and reload data.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;

        // Cancel any pending auto-save so stale entries from the old company
        // are not written into the new company's timesheet.
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }

        // Reset timesheet entries to prevent old company data from persisting
        setEntries(EMPTY_ENTRIES);

        // Reset paginated projects state, the cost centres cache and the
        // supplementary project lookups
        resetProjectsForCompanyChange();

        // Reset scheduled bookings and absences so they are re-fetched for the new company
        setHasLoadedScheduledBookings(false);
        setHasLoadedAbsences(false);

        // Reload projects for the new company
        loadInitialProjects();
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [
    loadInitialProjects,
    resetProjectsForCompanyChange,
    setEntries,
    setHasLoadedScheduledBookings,
    setHasLoadedAbsences,
    autoSaveTimerRef,
  ]);

  return {
    organizationSettings,
    employeeWorkingPattern,
    allAvailableProjects,
    isSearchingProjects,
    costCentresByProjectCode,
    handleProjectSearch,
    fetchCostCentresForProject,
    clearFetchedCostCentresCache,
    hasLoadedScheduledBookings,
    setHasLoadedScheduledBookings,
    hasLoadedAbsences,
    setHasLoadedAbsences,
  };
}
